/**
 * Wires gameStore state into assistantStore: when it becomes the hero's
 * turn and assistantMode includes 'suggest', a new suggestion is pushed.
 * Feedback is pushed when the hero's latest action is posted (naive
 * good/okay/mistake gradient based on GTO delta).
 *
 * Suggestions/feedback calculations use the cheapest offline path —
 * GTO chart lookup for preflop and a small equity MC postflop. LLM
 * integrations can layer on top once Settings exposes keys.
 */
import { useEffect, useRef } from 'react';
import { createPokersolverEvaluator } from '@/engine/handEvaluator';
import type { HandEvaluator } from '@/engine/ports';
import { createRng } from '@/engine/rng';
import { legalActions } from '@/engine/rules';
import type { Action, HandState, PlayerId } from '@/engine/types';
import { PERSONALITIES } from '@/gto/personality';
import { createGtoBot } from '@/gto/solver';
import { useAssistantStore } from '@/state/assistantStore';
import { useGameStore } from '@/state/gameStore';
import { useSettingsStore } from '@/state/settingsStore';

export type UseAssistantOptions = {
  heroId: PlayerId;
};

function isSuggestMode(mode: string): boolean {
  return mode === 'suggest' || mode === 'both';
}

function isFeedbackMode(mode: string): boolean {
  return mode === 'feedback' || mode === 'both';
}

function gtoAction(hand: HandState, heroId: PlayerId, evaluator: HandEvaluator): Action | null {
  const legals = legalActions(hand, heroId);
  if (legals.length === 0) return null;
  const rng = createRng(`assistant:${hand.seed}:${hand.handNumber}`);
  try {
    const bot = createGtoBot(PERSONALITIES.ORACLE, { rng, evaluator, trials: 400 });
    const decision = bot.decide(hand, heroId, legals);
    // GTO bot returns sync Action, but Bot port allows Promise — narrow.
    return decision instanceof Promise ? null : decision;
  } catch {
    return null;
  }
}

function describeAction(action: Action): string {
  switch (action.type) {
    case 'fold':
      return '폴드 권장';
    case 'check':
      return '체크 권장';
    case 'call':
      return `콜 ${action.amount}`;
    case 'bet':
      return `벳 ${action.amount}`;
    case 'raise':
      return `레이즈 ${action.to}`;
    case 'allin':
      return `올인 ${action.amount}`;
  }
}

function actionsEquivalent(a: Action, b: Action): 'good' | 'okay' | 'mistake' {
  if (a.type === b.type) {
    if (a.type === 'fold' || a.type === 'check') return 'good';
    if (a.type === 'call' || a.type === 'bet' || a.type === 'allin') {
      const amt = (x: Action) => (x.type === 'raise' ? x.to : 'amount' in x ? x.amount : 0);
      const diff = Math.abs(amt(a) - amt(b));
      if (diff === 0) return 'good';
      if (diff < 80) return 'okay';
      return 'mistake';
    }
    if (a.type === 'raise') {
      const diff = Math.abs(a.to - (b.type === 'raise' ? b.to : 0));
      if (diff === 0) return 'good';
      if (diff < 80) return 'okay';
      return 'mistake';
    }
    return 'okay';
  }
  // fold when GTO says something else is usually a mistake.
  if (a.type === 'fold' && b.type !== 'fold') return 'mistake';
  if (b.type === 'fold' && a.type !== 'fold') return 'mistake';
  return 'okay';
}

export function useAssistant({ heroId }: UseAssistantOptions): void {
  const hand = useGameStore((s) => s.hand);
  const pendingActor = hand?.pendingActor ?? null;
  const actionCount = hand?.actionHistory.length ?? 0;
  const mode = useSettingsStore((s) => s.assistantMode);
  const lastSuggestedKey = useRef<string | null>(null);
  const lastFeedbackKey = useRef<string | null>(null);

  // Suggest on hero turn.
  useEffect(() => {
    if (!hand || pendingActor !== heroId || !isSuggestMode(mode)) return;
    const key = `${hand.handNumber}:${actionCount}:suggest`;
    if (lastSuggestedKey.current === key) return;
    lastSuggestedKey.current = key;
    const evaluator = createPokersolverEvaluator();
    const action = gtoAction(hand, heroId, evaluator);
    if (!action) return;
    useAssistantStore.getState().pushSuggestion({
      handNumber: hand.handNumber,
      text: describeAction(action),
      createdAt: Date.now(),
    });
  }, [hand, pendingActor, heroId, mode, actionCount]);

  // Feedback after hero action.
  useEffect(() => {
    if (!hand || !isFeedbackMode(mode)) return;
    const lastAction = hand.actionHistory[hand.actionHistory.length - 1];
    if (!lastAction || lastAction.playerId !== heroId) return;
    const key = `${hand.handNumber}:${actionCount}:feedback`;
    if (lastFeedbackKey.current === key) return;
    lastFeedbackKey.current = key;
    // Recompute GTO from the pre-action state by rewinding — cheap approximation:
    // use current hand (post-action) is fine for V1; a proper rewind arrives in M6.
    const evaluator = createPokersolverEvaluator();
    const gto = gtoAction(hand, heroId, evaluator);
    const verdict = gto ? actionsEquivalent(lastAction, gto) : 'okay';
    useAssistantStore.getState().pushFeedback({
      handNumber: hand.handNumber,
      verdict,
      text: gto ? `${verdict} (GTO: ${describeAction(gto)})` : verdict,
      createdAt: Date.now(),
    });
  }, [hand, heroId, mode, actionCount]);
}
